import {assert, assertAndContinue} from 'Asserts';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import labAutoResultsTableToTable from 'lab-automation/results-table/labAutoResultsTableToTable';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {isFieldTypeSupportedForManualEntry} from 'notebook/data-tables/DataTableConfigEditUtil';
import {
  ResultsRowMetadata,
  ResultsTable,
  ResultsTableMetadata,
} from 'notebook/integrations/results/ResultsTableConstants';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {getAllSamplesByColumn} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SimpleSampleFilters} from 'notebook/sample-propagation/SampleFilter';
import getSampleId from 'notebook/sample-propagation/util/getSampleId';
import {addAPIPendingValuesActionFunction} from 'notebook/structured-tables/action-functions/addAPIPendingValuesActionFunction';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {createDatasetWithPlotsActionFunction} from 'notebook/structured-tables/action-functions/createDatasetWithPlotsActionFunction';
import {createRequestActionFunction} from 'notebook/structured-tables/action-functions/createRequestActionFunction';
import {exportAuditLogsActionFunction} from 'notebook/structured-tables/action-functions/exportAuditLogsActionFunction';
import {submitResultsActionFunction} from 'notebook/structured-tables/action-functions/submitResultsActionFunction';
import submitResultsRowsActionFunction from 'notebook/structured-tables/action-functions/submitResultsRowsActionFunction';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {ResultsTableRowUpdater} from 'notebook/structured-tables/row-updaters/ResultsTableRowUpdater';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {
  ActionFunctions,
  EvalRowView,
  EvalTableView,
  ExistingBioentityStructuredTableColumn,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableColumnSummaryRequirement,
  StructuredTableSchema,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {isRowArchived} from 'notebook/tables/util/isRowArchived';
import {createProcessActionFunction} from 'processes/tables/action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from 'processes/tables/action-functions/createRequestV2SubmissionActionFunction';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {
  FieldType,
  getRequiredLinkStorableSchema,
  getRequiredLinkTagSchema,
  isBioentityLinkFieldWithTargetSchema,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {isResultSchema} from 'schemas/SchemaPredicates';
import removeNulls from 'util/removeNulls';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import archiveResultsActionFunction from './archiveResultsActionFunction';
import {
  exportResultsAuditLogsActionFunction,
  exportResultsTableAuditLogsActionFunction,
} from './exportResultsAuditLogsActionFunction';

/**
 * ResultsTableAdapter is used only for the standalone results table, not the lab automation results table.
 * The lab auto table renders using its own ResultsTableView and labAutoResultsTableToTable function.
 */
export default class ResultsTableAdapter extends GenericStructuredTableAdapter<
  ResultsTableMetadata,
  ResultsRowMetadata
> {
  getDisplayName(_: ResultsTable): string {
    return 'Results';
  }

  override getRowMetadata(_: ResultsTableMetadata, rowData: RowData): ResultsRowMetadata {
    // To be consistent with other structured tables, this adapter uses the default toTable behavior of
    // storing our custom row metadata in rowData.rowMetadata.
    //
    // The lab auto results table view currently uses a custom toTable function, labAutoResultsTableToTable,
    // which _spreads_ the metadata into rowData, rather than storing it in rowData.rowMetadata.
    //
    // When coming fromTable, we read from both places to be compatible with both tables. This technically
    // won't be required unless we make the lab auto results table editable, but we add it here for the
    // avoidance of doubt.
    return {euid: (rowData.rowMetadata as ResultsRowMetadata).euid};
  }

  getBlankRowMetadata(tableMetadata: ResultsTableMetadata): ResultsRowMetadata {
    return {};
  }

  override toTable(resultsTable: ResultsTable): Table {
    if (resultsTable.metadata.supportsManualEntry) {
      return super.toTable(resultsTable);
    } else {
      const {table, widths} = labAutoResultsTableToTable(resultsTable);
      return {...table, widths};
    }
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: EvalRowView<ResultsRowMetadata>,
    structuredTable: EvalTableView<ResultsTableMetadata, ResultsRowMetadata>
  ): boolean {
    if (!structuredTable.metadata.supportsManualEntry) {
      return true;
    }
    return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
  }

  // Keep in sync with result_table_instantiator.py
  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ResultsTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return super
      ._getAllSupportedColumns({entryId, metadata, schemas, selectorsById, structuredTableInsertionContext})
      .filter((column) => {
        const fieldType = (column as TagValueStructuredTableColumn).schemaFieldType;

        // Deleted fields are only expected in DataTableConfigs (see docs on
        // BaseTagValueStructuredTableColumnWithMissingField)
        if (!assertAndContinue(fieldType != null, 'Found unexpected column for a deleted schema field')) {
          return false;
        }

        const isSnapshotField = (column as TagValueStructuredTableColumn).schemaFieldIsSnapshot;
        return isFieldTypeSupportedForManualEntry(fieldType!) || isSnapshotField;
      });
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: ResultsTableMetadata;
  }): StructuredTableColumn {
    if (schemas.length !== 1) {
      return column;
    }
    const [schema] = schemas;
    assert(isResultSchema(schema), 'Results table adapter invoked with non-result schema');
    const primaryLinkField = getPrimaryLinkField(schema);

    if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
      assert(primaryLinkField != null, 'Unexpected existing bioentity column in results table with no PLF');
      return {
        ...column,
        frontEndMetadata: {
          validSchemas: isBioentityLinkFieldWithTargetSchema(primaryLinkField)
            ? [primaryLinkField.requiredLink.tagSchema]
            : 'ALL',
        },
      } as ExistingBioentityStructuredTableColumn;
    }
    return column;
  }

  // Keep in sync with ResultTableInstantiator.get_primary_sample_ids_for_row in
  // benchling/notebook/structured_tables/instantiators/result_table_instantiator.py (tested by
  // TestResultInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/result_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: ResultsTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const primarySamplesByRowId = {} as {
      [rowId: string]: Array<PrimarySample>;
    };
    for (const row of structuredTable.rows) {
      const resultId = row.metadata.euid;
      // If the euid is not defined, this means the row has never been finalized and the result has never been
      // created. Thus, the row does not have any primary samples.
      if (resultId == null) {
        primarySamplesByRowId[row.rowId] = [];
      } else {
        primarySamplesByRowId[row.rowId] = [
          {
            schemaId: null,
            columnGroup: null,
            sampleLookupInfo: {sampleType: PrimarySampleLookupType.DIRECT, idForLookup: resultId},
          },
        ];
      }
    }
    return primarySamplesByRowId;
  }

  override getTableKindRowUpdater(): typeof ResultsTableRowUpdater {
    return ResultsTableRowUpdater;
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: ResultsTable,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<StructuredTableAddOperation<ResultsTableMetadata, ResultsRowMetadata>> {
    const ops: Array<StructuredTableAddOperation<ResultsTableMetadata, ResultsRowMetadata>> = [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
        onSelect: ({note, structuredTable, addSamples}) => {
          addSamplesFromStructuredTable({
            destinationTable: structuredTable,
            adapter: this,
            note,
            entryId,
            addSamples,
          });
        },
      },
    ];
    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(
            structuredTable,
            this,
            addSamples,
            WORKLIST_FILL_LOCATION.STANDALONE_BIOENTITY_RESULTS_TABLE
          );
        },
      });
    }
    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
      onSelect: async ({structuredTable, addFullRows}) => {
        const [resultSchema] = structuredTable.schemas;
        const rows = await importRowsFromSpreadsheet({
          structuredTable,
          schemas: {resultSchema: resultSchema as ResultSchema},
          getSpreadsheetTypeForColumn: (column) =>
            getSpreadsheetTypeForColumn(ModelTypeConstants.RESULT_SCHEMA, column),
        });

        if (rows) {
          addFullRows(rows);
        }
      },
    });
    if (BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_USING_AI,
        onSelect: ({structuredTable}) => {
          addAIDataEntryRows?.(undefined, structuredTable.name);
        },
      });
    }
    return ops;
  }

  override getSampleInfosForRequest(
    structuredTable: ResultsTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): Array<SampleLookupInfoForCreateRequest> {
    const sourceTable = this.toTable(structuredTable);
    return getAllSamplesByColumn(
      sourceTable,
      [SimpleSampleFilters.BATCH, SimpleSampleFilters.BIOENTITY, SimpleSampleFilters.CONTAINER],
      undefined,
      {
        skipLinkedSamples: false,
        removeDuplicates: true,
        includeUnfinalizedRows: false,
      }
    )
      .flatMap((columnSamples) => removeNulls(columnSamples.samples.map((sample) => getSampleId(sample))))
      .map((sampleId) => ({
        idForLookup: sampleId,
      }));
  }

  override getActionFunctions(
    tableMetadata: ResultsTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<ResultsTableMetadata, ResultsRowMetadata> {
    if (tableMetadata.supportsManualEntry) {
      const {secondary, overflow, footer} = super.getActionFunctions(tableMetadata, schemas);

      assertAndContinue(
        [...(secondary ?? []), ...(overflow ?? [])].filter(
          (actionFunction) => actionFunction === exportAuditLogsActionFunction
        ).length === 1,
        'Expected `exportAuditLogsActionFunction` to be included exactly once'
      );

      return {
        primary: [submitResultsActionFunction],
        secondary: [
          ...(secondary ?? []),
          addAPIPendingValuesActionFunction,
          createRequestV2SubmissionActionFunction,
          createProcessActionFunction,
          addToWorklistActionFunction,
          createRequestActionFunction,
          createDatasetWithPlotsActionFunction,
        ],
        overflow: [
          ...[...(overflow ?? [])].filter(
            (actionFunction) => actionFunction !== exportAuditLogsActionFunction
          ),
          exportResultsTableAuditLogsActionFunction,
          exportResultsAuditLogsActionFunction,
        ],
        contextMenu: [submitResultsRowsActionFunction, archiveResultsActionFunction],
        footer: footer ?? [],
      };
    } else {
      return {
        secondary: [
          addAPIPendingValuesActionFunction,
          addToWorklistActionFunction,
          createDatasetWithPlotsActionFunction,
        ],
      };
    }
  }

  override getColumnSummariesForCreateProcessShortcut(
    structuredTable: ResultsTable
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    const BIOENTITY_COLUMN_SUMMARY_REQUIREMENTS: Array<StructuredTableColumnSummaryRequirement> = [
      {
        columnType: TableColumnType.TAG_VALUE,
        schemaFieldType: FieldType.BIOENTITY_LINK,
        folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
      },
      {
        columnType: TableColumnType.EXISTING_BIOENTITY,
        schemaFieldType: null,
        folderItemType: null,
      },
    ];

    const summaries: Array<StructuredTableColumnSummary & {columnIndex: number}> = [];

    // The primary column is the leftmost entity or storable column, if one exists
    for (const [columnIndex, column] of structuredTable.columns.entries()) {
      if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
        summaries.push({
          validDestinationColumnRequirements: BIOENTITY_COLUMN_SUMMARY_REQUIREMENTS,
          schemaIdConstraint: null,
          columnIndex,
        });
      }
      if (column.columnType === TableColumnType.TAG_VALUE) {
        const schemaFieldType = (column as TagValueStructuredTableColumn).schemaFieldType;
        if (!schemaFieldType) {
          continue;
        }
        const schemaField =
          structuredTable.schemas[0].fields.find((field) => column.schemaFieldApiId === field.apiId) ?? null;
        if (schemaFieldType === FieldType.BIOENTITY_LINK) {
          summaries.push({
            validDestinationColumnRequirements: BIOENTITY_COLUMN_SUMMARY_REQUIREMENTS,
            schemaIdConstraint: schemaField && (getRequiredLinkTagSchema(schemaField)?.id ?? null),
            columnIndex,
          });
        }
        if (schemaFieldType === FieldType.STORABLE_LINK) {
          summaries.push({
            validDestinationColumnRequirements: [
              {
                columnType: TableColumnType.TAG_VALUE,
                schemaFieldType: FieldType.STORABLE_LINK,
                folderItemType: null,
              },
              {columnType: TableColumnType.CONTAINERS, schemaFieldType: null, folderItemType: null},
            ],
            schemaIdConstraint: schemaField && (getRequiredLinkStorableSchema(schemaField)?.id ?? null),
            columnIndex,
          });
        }
      }
    }
    return summaries;
  }

  override isReadOnly(): boolean {
    return !isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
  }

  // Keep in sync with ResultTableInstantiator.allow_delete_table in
  // benchling/notebook/structured_tables/instantiators/result_table_instantiator.py
  // Intentionally diverges in checking archived rows: backend always uses AssayResult.archive_record_id
  override allowDeleteTable(structuredTable: ResultsTable): {
    canDelete: boolean;
    reason?: string;
  } {
    const superResult = super.allowDeleteTable(structuredTable);
    if (!superResult.canDelete) {
      return superResult;
    }
    // We can only delete a table if there are no finalized rows which aren't archived.
    if (
      !structuredTable.rows
        .filter((row) => row.isFinalized)
        // Due to a bug with table cut/paste we may have rows with no euid. These rows can not be archived
        // and are no longer associated with a result. We can safely delete these rows.
        .every((rowData) => isRowArchived(rowData) || (rowData.metadata && rowData.metadata.euid == null))
    ) {
      return {canDelete: false, reason: 'Please archive all Results before deleting the table'};
    }
    return {canDelete: true};
  }

  override enableCustomColumns(structuredTable: ResultsTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: ResultsTable): boolean {
    return true;
  }
}
